/**
 * InvoiceMapping — stage 4/5 for Purchase Invoice & Sales Invoice.
 * Purchase: header = Supplier (Cr), item lines = real Item Master (auto-resolves
 *           the item's PurchaseAccount GL), tax/charge lines = manual GL (Dr).
 * Sales:    header = Customer (Dr), item lines = real Item Master (auto-resolves
 *           the item's SalesAccount GL), tax/charge lines = manual GL (Cr).
 * Full product detail (HSN/Qty/UOM/Rate/GST%/Total) is shown for every row so
 * the user can visually verify the OCR grid before confirming — not just a
 * bare description + amount.
 */
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Search, Package, CheckCircle2 } from 'lucide-react';
import {API_BASE, getToken} from '../../lib/api';
const API = API_BASE;

const money = v => (v || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

export default function InvoiceMapping({ extracted, mapping, setMapping, isDark, border, bgCard, text, textMut, isSales, isSalesReturn }) {
  // isSalesReturn: party = Customer, items = DrAmount (reversed from sales_invoice)
  const isCustomerParty = isSales || isSalesReturn;
  const [parties, setParties] = useState([]);
  const [partySearch, setPartySearch] = useState(extracted.partyName || '');
  const [selectedParty, setSelectedParty] = useState(null);
  const [glAccounts, setGlAccounts] = useState([]);

  const lineItems = extracted.lineItems || [];
  const taxLines = extracted.taxLines || [];

  // Per-row item-master mapping state (product lines)
  const [itemSearch, setItemSearch] = useState(lineItems.map(li => li.description || ''));
  const [itemResults, setItemResults] = useState(lineItems.map(() => []));
  const [selectedItems, setSelectedItems] = useState(lineItems.map(() => null));
  const [manualGlForItem, setManualGlForItem] = useState(lineItems.map(() => '')); // fallback if item has no account configured
  const debounceRefs = useRef([]);

  // Per-row GL mapping state (tax/charge lines)
  const [taxGl, setTaxGl] = useState(taxLines.map(() => ''));

  useEffect(() => {
    const endpoint = isCustomerParty ? '/api/customer-master' : '/api/supplier-master';
    fetch(`${API}${endpoint}`, { headers: { Authorization: `Bearer ${getToken()}` } })
      .then(r => r.json()).then(d => {
        let list = d.data || [];
        if (Array.isArray(list[0])) list = list.flat(3); // .NET nested array quirk
        setParties(list);
      }).catch(() => {});
    fetch(`${API}/api/import/accounts?limit=400`, { headers: { Authorization: `Bearer ${getToken()}` } })
      .then(r => r.json()).then(d => { if (d.success) setGlAccounts(d.data); }).catch(() => {});
  }, [isCustomerParty]);

  const partyId = p => p?.SupplierId || p?.SUPPID || p?.Vend_uid || p?.CustomerId || p?.CUSTOMERID || p?.CustID || '';
  const partyName = p => p?.SupplierName || p?.SUPPNAME || p?.Vend_name || p?.CustomerName || p?.CUSTOMERNAME || p?.CustomerName || '';
  const glName = id => glAccounts.find(a => a.GLId === id)?.GLName || id;

  const filteredParties = parties.filter(p => partyName(p).toLowerCase().includes(partySearch.toLowerCase())).slice(0, 30);

  const searchItems = useCallback((idx, q) => {
    setItemSearch(s => { const arr = [...s]; arr[idx] = q; return arr; });
    setSelectedItems(s => { const arr = [...s]; arr[idx] = null; return arr; });
    clearTimeout(debounceRefs.current[idx]);
    if (q.trim().length < 2) { setItemResults(r => { const arr = [...r]; arr[idx] = []; return arr; }); return; }
    debounceRefs.current[idx] = setTimeout(() => {
      fetch(`${API}/api/document-hub/items?q=${encodeURIComponent(q)}`, { headers: { Authorization: `Bearer ${getToken()}` } })
        .then(r => r.json()).then(d => setItemResults(res => { const arr = [...res]; arr[idx] = d.data || []; return arr; })).catch(() => {});
    }, 350);
  }, []);

  // Resolved GL account for an item line: item's own Purchase/SalesAccount, else the manual fallback dropdown
  const resolvedItemGl = (idx) => {
    const it = selectedItems[idx];
    const auto = it ? (isSales ? it.SalesAccount : it.PurchaseAccount) : '';
    return auto || manualGlForItem[idx] || '';
  };

  const buildMapping = useCallback(() => {
    if (!selectedParty) { setMapping(m => ({ ...m, __valid: false })); return; }
    const isPurchase = !isSales && !isSalesReturn;

    const itemLines = lineItems.map((li, i) => {
      const matched = selectedItems[i];
      const ac = resolvedItemGl(i);
      return {
        // GL-voucher fields (sales / fallback)
        ACId: ac,
        description: li.description,
        amount: li.amount,
        DrAmount: isSales ? 0 : li.amount,
        CrAmount: isSales ? li.amount : 0,
        // Full item detail fields (for real PI/SI API)
        ItemCode: matched?.Itemcode || '',
        ItemDesc: li.description,
        HSN: li.hsn || matched?.HSNCode || '',
        Qty: li.qty || 1,
        UOM: li.uom || matched?.UOMId || 'PCS',
        Rate: li.rate || li.amount,
        GSTRate: li.gstPct || 0,
        Amount: li.amount,
        PurchaseAccount: matched?.PurchaseAccount || '',
        SalesAccount: matched?.SalesAccount || '',
      };
    });
    const taxLineEntries = taxLines.map((tl, i) => ({
      ACId: taxGl[i], description: tl.description, amount: tl.amount,
      DrAmount: isSalesReturn ? tl.amount : (isSales ? 0 : tl.amount),
      CrAmount: isSalesReturn ? 0 : (isSales ? tl.amount : 0),
    }));
    const items = [...itemLines, ...taxLineEntries];

    // Validation:
    // For purchase invoices → supplier + items (no GL required, PI API handles accounting)
    // For sales/sales-return → supplier + GL accounts per line (GL voucher approach)
    let allMapped;
    if (isPurchase) {
      allMapped = itemLines.length > 0; // just need supplier + at least 1 item
    } else {
      const itemsMapped = itemLines.length > 0 && itemLines.every(it => it.ACId);
      const taxMapped = taxLineEntries.every(it => it.ACId);
      allMapped = items.length > 0 && itemsMapped && taxMapped;
    }

    const total = itemLines.reduce((s, it) => s + (it.amount || 0), 0);
    const taxTotal = taxLineEntries.reduce((s, it) => s + (it.amount || 0), 0);
    const grandTotal = total + taxTotal;

    // Select the right endpoint
    const endpoint = isPurchase
      ? '/api/PurchaseInvoice/api/v1/Save'
      : (isSalesReturn ? '/api/vouchers/sales-return' : '/api/vouchers/sales-voucher');

    const voucherLabel = isSalesReturn ? 'Sales Return' : (isSales ? 'Sales Invoice' : 'Purchase Invoice');
    setMapping({
      __valid: allMapped,
      saveEndpoint: endpoint,
      savePayload: isPurchase ? {
        // Purchase Invoice API payload — full enriched fields
        VendorId: partyId(selectedParty),
        InvNo: extracted.invoiceNo || '',
        InvDate: extracted.invoiceDate || new Date().toISOString().slice(0, 10),
        GSTIN: extracted.supplierGSTIN || extracted.gstin || '',
        supplierGSTIN: extracted.supplierGSTIN || extracted.gstin || '',
        buyerGSTIN: extracted.buyerGSTIN || '',
        buyerName: extracted.buyerName || '',
        transport: extracted.transport || '',
        eWayBillNo: extracted.eWayBillNo || '',
        irn: extracted.irn || '',
        ackNo: extracted.ackNo || '',
        taxableValue: extracted.taxableValue || 0,
        cgstAmt: extracted.cgstAmt || 0,
        sgstAmt: extracted.sgstAmt || 0,
        igstAmt: extracted.igstAmt || 0,
        GrandTotal: extracted.grandTotal || grandTotal,
        items: itemLines,
        taxLines: taxLineEntries.map(tl => ({ description: tl.description, rate: tl.rate || 0, amount: tl.amount })),
        Remarks: `Purchase Invoice ${extracted.invoiceNo || ''} — Document Hub OCR Import`,
        // Fallback GL fields (used if .NET API down)
        ACId: partyId(selectedParty),
        DocDate: extracted.invoiceDate || new Date().toISOString().slice(0, 10),
        CommNarration: `Purchase Invoice ${extracted.invoiceNo || ''} — Document Hub OCR`,
      } : {
        // GL Voucher payload (sales_invoice / sales_return)
        ACId: partyId(selectedParty),
        DocDate: extracted.invoiceDate || new Date().toISOString().slice(0, 10),
        CommNarration: `${voucherLabel} ${extracted.invoiceNo || ''} — Document Hub OCR`.trim(),
        items,
      },
      summary: [
        `${isCustomerParty ? 'Customer' : 'Supplier'}: ${partyName(selectedParty)} (${partyId(selectedParty)})`,
        `Invoice No: ${extracted.invoiceNo || 'N/A'} · Date: ${extracted.invoiceDate || 'N/A'}`,
        isPurchase
          ? `${itemLines.length} item(s) · Grand Total: ₹${money(extracted.grandTotal || grandTotal)}`
          : `${itemLines.length} item(s) + ${taxLineEntries.length} tax/charge line(s) · Total: ${money(total)}`,
      ],
      warning: !allMapped ? (
        isPurchase
          ? undefined // purchase just needs supplier
          : (!itemLines.every(it => it.ACId) ? 'Every product line needs an Item Master match (or fallback GL) before you can continue.' : 'Every tax/charge line needs a GL account selected before you can continue.')
      ) : undefined,
    });
  }, [selectedParty, selectedItems, manualGlForItem, taxGl, extracted, isSales, isSalesReturn, isCustomerParty, setMapping]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { buildMapping(); }, [selectedParty, selectedItems, manualGlForItem, taxGl]); // eslint-disable-line react-hooks/exhaustive-deps

  const gridCols = [
    { key: 'description', label: 'Product / Description', flex: 2.2 },
    { key: 'hsn', label: 'HSN', flex: 0.9 },
    { key: 'qty', label: 'Qty', flex: 0.7 },
    { key: 'uom', label: 'UOM', flex: 0.7 },
    { key: 'rate', label: 'Rate', flex: 0.9 },
    { key: 'gstPct', label: 'GST %', flex: 0.7 },
    { key: 'amount', label: 'Total Value', flex: 1 },
  ];

  return (
    <div>
      {/* ── Extracted Invoice Header Summary (for visual verification) ─── */}
      <div style={{ marginBottom: 14, padding: '10px 14px', borderRadius: 10, border: `1px solid ${border}`, background: isDark ? '#111827' : '#f0f9ff', fontSize: 11 }}>
        <div style={{ fontWeight: 700, color: textMut, marginBottom: 8, textTransform: 'uppercase', fontSize: 10 }}>Extracted Invoice Details — Verify Before Saving</div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '4px 16px' }}>
          {[
            ['Invoice No', extracted.invoiceNo],
            ['Invoice Date', extracted.invoiceDate],
            ['Supplier (Seller)', extracted.supplierName || extracted.partyName],
            ['Supplier GSTIN', extracted.supplierGSTIN || extracted.gstin],
            ['Buyer (Bill To)', extracted.buyerName],
            ['Buyer GSTIN', extracted.buyerGSTIN],
            ['Transport', extracted.transport],
            ['E-Way Bill No', extracted.eWayBillNo],
            ['IRN', extracted.irn ? (extracted.irn.length > 30 ? extracted.irn.slice(0, 30) + '…' : extracted.irn) : null],
            ['Ack No', extracted.ackNo],
          ].filter(([, v]) => v).map(([label, val]) => (
            <div key={label} style={{ display: 'flex', gap: 6 }}>
              <span style={{ color: textMut, minWidth: 110 }}>{label}:</span>
              <span style={{ color: text, fontWeight: label.includes('GSTIN') ? 600 : 400, wordBreak: 'break-all' }}>{val}</span>
            </div>
          ))}
        </div>
        {/* Tax summary footer */}
        {(extracted.taxableValue || extracted.cgstAmt || extracted.sgstAmt || extracted.igstAmt || extracted.grandTotal) ? (
          <div style={{ marginTop: 8, paddingTop: 6, borderTop: `1px solid ${border}`, display: 'flex', gap: 20, flexWrap: 'wrap' }}>
            {extracted.taxableValue ? <span><span style={{ color: textMut }}>Taxable: </span><b>₹{money(extracted.taxableValue)}</b></span> : null}
            {extracted.cgstAmt ? <span><span style={{ color: textMut }}>CGST: </span><b>₹{money(extracted.cgstAmt)}</b></span> : null}
            {extracted.sgstAmt ? <span><span style={{ color: textMut }}>SGST: </span><b>₹{money(extracted.sgstAmt)}</b></span> : null}
            {extracted.igstAmt ? <span><span style={{ color: textMut }}>IGST: </span><b>₹{money(extracted.igstAmt)}</b></span> : null}
            {extracted.grandTotal ? <span><span style={{ color: textMut }}>Grand Total: </span><b style={{ color: '#22c55e' }}>₹{money(extracted.grandTotal)}</b></span> : null}
          </div>
        ) : null}
        {extracted._geminiUsed && <div style={{ marginTop: 6, fontSize: 10, color: '#a78bfa' }}>AI-enhanced extraction (Gemini)</div>}
        {extracted._sarvamStructured && <div style={{ marginTop: 6, fontSize: 10, color: '#22c55e' }}>Sarvam AI structured extraction — high accuracy</div>}
      </div>

      <div style={{ marginBottom: 16 }}>
        <div style={{ fontSize: 11, fontWeight: 700, color: textMut, marginBottom: 6 }}>{isCustomerParty ? 'CUSTOMER' : 'SUPPLIER'}</div>
        <div style={{ position: 'relative' }}>
          <Search size={12} style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: textMut }} />
          <input data-testid="doc-hub-party-search" value={partySearch} onChange={e => { setPartySearch(e.target.value); setSelectedParty(null); }}
            placeholder={`Search ${isCustomerParty ? 'customer' : 'supplier'}...`}
            style={{ width: '100%', padding: '9px 10px 9px 28px', borderRadius: 8, border: `1px solid ${selectedParty ? '#22c55e' : border}`, background: bgCard, color: text, fontSize: 12, boxSizing: 'border-box', outline: 'none' }} />
        </div>
        {!selectedParty && partySearch && (
          <div style={{ marginTop: 6, maxHeight: 160, overflowY: 'auto', border: `1px solid ${border}`, borderRadius: 8 }}>
            {filteredParties.map((p, i) => (
              <div key={i} data-testid={`doc-hub-party-option-${i}`} onClick={() => { setSelectedParty(p); setPartySearch(partyName(p)); }}
                style={{ padding: '8px 12px', fontSize: 12, color: text, cursor: 'pointer', borderTop: i > 0 ? `1px solid ${border}` : 'none' }}
                onMouseEnter={e => { if (!e.currentTarget) return; e.currentTarget.style.background = isDark ? '#1a1a1a' : '#f8fafc'; }} onMouseLeave={e => { if (!e.currentTarget) return; e.currentTarget.style.background = 'transparent'; }}>
                {partyName(p)} <span style={{ color: textMut }}>({partyId(p)})</span>
              </div>
            ))}
            {filteredParties.length === 0 && <div style={{ padding: '8px 12px', fontSize: 11, color: textMut }}>No matches</div>}
          </div>
        )}
      </div>

      <div style={{ fontSize: 11, fontWeight: 700, color: textMut, marginBottom: 6 }}>PRODUCT / ITEM MASTER MAPPING</div>
      <div style={{ border: `1px solid ${border}`, borderRadius: 10, overflow: 'hidden', marginBottom: taxLines.length ? 16 : 0 }}>
        <div style={{ display: 'flex', padding: '7px 12px', background: isDark ? '#1a1a1a' : '#f8fafc', borderBottom: `1px solid ${border}` }}>
          {gridCols.map(c => <div key={c.key} style={{ flex: c.flex, fontSize: 9, fontWeight: 800, color: textMut, textTransform: 'uppercase' }}>{c.label}</div>)}
        </div>
        {lineItems.map((li, i) => {
          const matched = selectedItems[i];
          const resolvedGl = resolvedItemGl(i);
          return (
            <div key={i} style={{ borderTop: i > 0 ? `1px solid ${border}` : 'none', padding: '9px 12px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 4, marginBottom: li.sizeBreakdown ? 4 : 8 }}>
                {gridCols.map(c => (
                  <div key={c.key} style={{ flex: c.flex, fontSize: 12, color: c.key === 'description' ? text : textMut, fontFamily: c.key === 'description' ? 'inherit' : 'monospace', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontWeight: c.key === 'amount' ? 700 : 400 }}>
                    {c.key === 'amount' ? money(li.amount) : c.key === 'rate' ? (li.rate ? money(li.rate) : '—') : c.key === 'gstPct' ? (li.gstPct ? `${li.gstPct}%` : '—') : (li[c.key] || '—')}
                  </div>
                ))}
              </div>
              {li.sizeBreakdown && (
                <div style={{ fontSize: 10, color: textMut, marginBottom: 6, fontFamily: 'monospace', paddingLeft: 2 }}>
                  Sizes: {li.sizeBreakdown}
                </div>
              )}
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <Package size={12} color={matched ? '#22c55e' : textMut} style={{ flexShrink: 0 }} />
                <div style={{ position: 'relative', flex: 1 }}>
                  <input data-testid={`doc-hub-item-search-${i}`} value={itemSearch[i]} onChange={e => searchItems(i, e.target.value)}
                    placeholder="Search Item Master..."
                    style={{ width: '100%', padding: '6px 8px', borderRadius: 6, border: `1px solid ${matched ? '#22c55e' : border}`, background: bgCard, color: text, fontSize: 11, boxSizing: 'border-box', outline: 'none' }} />
                  {!matched && itemResults[i]?.length > 0 && (
                    <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 5, marginTop: 4, maxHeight: 160, overflowY: 'auto', border: `1px solid ${border}`, borderRadius: 8, background: bgCard, boxShadow: '0 4px 14px rgba(0,0,0,.2)' }}>
                      {itemResults[i].map((it, oi) => (
                        <div key={oi} data-testid={`doc-hub-item-option-${i}-${oi}`}
                          onClick={() => { setSelectedItems(s => { const a = [...s]; a[i] = it; return a; }); setItemSearch(s => { const a = [...s]; a[i] = it.ItemDesc; return a; }); setItemResults(r => { const a = [...r]; a[i] = []; return a; }); }}
                          style={{ padding: '7px 10px', fontSize: 11, color: text, cursor: 'pointer', borderTop: oi > 0 ? `1px solid ${border}` : 'none' }}
                          onMouseEnter={e => { if (!e.currentTarget) return; e.currentTarget.style.background = isDark ? '#1a1a1a' : '#f8fafc'; }} onMouseLeave={e => { if (!e.currentTarget) return; e.currentTarget.style.background = 'transparent'; }}>
                          {it.ItemDesc} <span style={{ color: textMut }}>({it.Itemcode} · HSN {it.HSNCode || 'N/A'})</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                {matched && <CheckCircle2 size={14} color="#22c55e" style={{ flexShrink: 0 }} />}
                {matched && !((isSales ? matched.SalesAccount : matched.PurchaseAccount)) && (
                  <select data-testid={`doc-hub-item-fallback-gl-${i}`} value={manualGlForItem[i]} onChange={e => setManualGlForItem(g => { const a = [...g]; a[i] = e.target.value; return a; })}
                    style={{ width: 170, fontSize: 10, padding: '5px 6px', borderRadius: 6, border: `1px solid ${manualGlForItem[i] ? '#22c55e' : border}`, background: bgCard, color: text, flexShrink: 0 }}>
                    <option value="">— no GL on item, pick one —</option>
                    {glAccounts.map(a => <option key={a.GLId} value={a.GLId}>{a.GLId} · {a.GLName}</option>)}
                  </select>
                )}
              </div>
              {matched && resolvedGl && (
                <div style={{ fontSize: 10, color: '#8b5cf6', marginTop: 5, marginLeft: 20 }}>→ posts to {glName(resolvedGl)} ({resolvedGl})</div>
              )}
            </div>
          );
        })}
        {lineItems.length === 0 && <div style={{ padding: 16, fontSize: 11, color: textMut, textAlign: 'center' }}>No product line items were extracted from this document.</div>}
      </div>

      {taxLines.length > 0 && (
        <>
          <div style={{ fontSize: 11, fontWeight: 700, color: textMut, marginBottom: 6 }}>TAX & OTHER CHARGES — GL MAPPING</div>
          <div style={{ border: `1px solid ${border}`, borderRadius: 10, overflow: 'hidden' }}>
            {taxLines.map((tl, i) => (
              <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 12px', borderTop: i > 0 ? `1px solid ${border}` : 'none' }}>
                <div style={{ flex: 1, fontSize: 12, color: text, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{tl.description}</div>
                <div style={{ fontSize: 11, color: '#f59e0b', fontFamily: 'monospace', width: 90, textAlign: 'right' }}>{money(tl.amount)}</div>
                <select data-testid={`doc-hub-tax-gl-${i}`} value={taxGl[i]} onChange={e => setTaxGl(g => { const a = [...g]; a[i] = e.target.value; return a; })}
                  style={{ width: 200, fontSize: 11, padding: '5px 6px', borderRadius: 6, border: `1px solid ${taxGl[i] ? '#22c55e' : border}`, background: bgCard, color: text }}>
                  <option value="">— GL account —</option>
                  {glAccounts.map(a => <option key={a.GLId} value={a.GLId}>{a.GLId} · {a.GLName}</option>)}
                </select>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
